import type { ChangeEvent, ComponentProps, FC } from 'react';
import { useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { FormSlider, Range } from '@catalyze/ui';

import { Container, Row, StyledFormInput } from './styled';

import { ClearButton } from '../ClearButton';

type IRangeMenuProps = ComponentProps<typeof Container> & {
    inputName: string;
    lowerBoundText: string;
    upperBoundText: string;
    min: number;
    max: number;
    disabled?: boolean;
};

export const RangeMenu: FC<IRangeMenuProps> = ({
    inputName,
    lowerBoundText,
    upperBoundText,
    min,
    max,
    disabled,
    ...props
}) => {
    const { watch, resetField } = useFormContext();
    const {
        field: { onChange, onBlur },
        fieldState: { isDirty },
    } = useController({ name: inputName });
    const [minValue, maxValue] = watch(
        [`${inputName}.${Range.MIN}`, `${inputName}.${Range.MAX}`],
        [min, max]
    ) as [string, string];

    const onLowerChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            const value = Number(ev.target.value);
            onChange({
                [Range.MIN]: value.toString(),
            });
            onBlur();
        },
        [onChange, onBlur]
    );

    const onUpperChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            const value = Number(ev.target.value);
            onChange({
                [Range.MAX]: value.toString(),
            });
            onBlur();
        },
        [onChange, onBlur]
    );

    return (
        <Container {...props}>
            <Row>
                <FormSlider
                    min={min}
                    max={max}
                    disabled={disabled}
                    id={inputName}
                />
            </Row>
            <Row style={{ marginBottom: 0 }}>
                <StyledFormInput
                    label={lowerBoundText}
                    id={`${inputName}.${Range.MIN}`}
                    min={min}
                    max={Number(maxValue) - 1}
                    disabled={disabled}
                    onChange={onLowerChange}
                    type="number"
                />
                <StyledFormInput
                    label={upperBoundText}
                    onChange={onUpperChange}
                    id={`${inputName}.${Range.MAX}`}
                    min={Number(minValue) + 1}
                    max={max}
                    disabled={disabled}
                    type="number"
                />
            </Row>
            <ClearButton
                disabled={!isDirty || disabled}
                onClick={() => resetField(inputName)}
            >
                Clear
            </ClearButton>
        </Container>
    );
};

RangeMenu.defaultProps = {
    disabled: false,
};
